import { WithFlyout } from "@amzn/stencil-react-components/flyout";
import { CheckLicenseCapOutput } from "lib/services/app-sense-service";
import { GetAssetStatusV1Output } from "lib/types/app-link";
import React from "react";

import { LicenseLimitFlyout } from "../LicenseLimitFlyout";
import {
  HasActiveLicenseBanner,
  HasOrderedItemBanner,
  LicenseLimitBanner,
  NonCompatibleOSBanner,
  ReturnInitiatedMessageBanner,
} from "../MessageBanners";

export interface ProductDetailsBannersProps {
  limitCap: CheckLicenseCapOutput;
  assetStatus: GetAssetStatusV1Output;
  isOsCompatible: boolean;
  hasOrderedItem?: string;
  productName: string;
}

export const ProductDetailsBanners: React.FC<ProductDetailsBannersProps> = ({
  limitCap,
  assetStatus,
  isOsCompatible,
  hasOrderedItem,
  productName,
}) => {
  /**
   * Renders a priority banner based on the asset's status.
   *
   * The order of checks is important:
   * 1. First, we check if the asset has a license, as this is the most important status.
   * 2. If there's a license, we then check if a return is in progress, as this takes precedence over just having a license.
   * 3. If there's no license, we check if the item has been ordered.
   *
   * Note: As per UX recommendation, we prioritize showing the 'active license' message
   * over the 'has order' message. This is because users can already see their
   * order status from the button on the right side of the interface.
   *
   * @returns {React.ReactElement|null} The appropriate banner component or null if no conditions are met.
   */
  const renderPriorityBanner = () => {
    if (assetStatus?.hasLicense) {
      if (assetStatus.returnStatus?.inProgress) {
        return <ReturnInitiatedMessageBanner />;
      }
      return <HasActiveLicenseBanner />;
    }
    if (hasOrderedItem) {
      return <HasOrderedItemBanner date={hasOrderedItem} />;
    }
    return null;
  };

  return (
    <div className="product-details-banners-container">
      {limitCap.isCapReached && (
        <WithFlyout renderFlyout={({ close }) => <LicenseLimitFlyout onClose={close} />}>
          {({ open }) => (
            <LicenseLimitBanner
              managerAlias={limitCap.closestLeaderWithExceededLimit}
              productName={productName}
              showFAQ={open}
            />
          )}
        </WithFlyout>
      )}

      {renderPriorityBanner()}
      {!isOsCompatible && <NonCompatibleOSBanner />}
    </div>
  );
};
